/**
 * Phase N2b — structure-driven order pipeline.
 *
 * The configurator's CHROME was de-hardcoded in N1a (step rail, merch tabs,
 * quantity tiers). The SUBMISSION layer was not: the cart-sync effect, the
 * checkout gate, and the submission builder still keyed on the literal
 * `packaging` / `merch` / `final-touch` step ids — so a box authored with a
 * novel step structure rendered and priced but HARD DEAD-ENDED at checkout
 * (its `cart.box` slots stayed empty, the gate never opened, and even if it
 * had, the submission summary fields would be blank). N1's adversarial review
 * flagged this HIGH.
 *
 * These pure helpers derive everything from the flow's OWN option steps:
 *
 *  - The first three OPTION steps map by POSITION onto the legacy
 *    `cart.box.{packagingId, merchIds, finalTouchIds}` slots. For the
 *    canonical Spring/Office archetype (packaging → merch → final-touch)
 *    this is byte-identical to the old literal mapping, so their cart,
 *    submission, and email are unchanged. For a novel box (vessel → treats →
 *    wrap) the slots are filled positionally, so checkout opens and a
 *    submission is produced.
 *  - A generic, self-describing `order.steps[]` array (added to
 *    `@brandbox/content-schema` in N2b) records EVERY option step's
 *    selections keyed by its real id + title, so the admin /submissions UI
 *    can render an arbitrary structure faithfully.
 *  - `maxSelections` is honoured per the schema convention
 *    (`0 = unlimited; 1 = single-choice; N = max-N`) — replacing N1a's
 *    index rule that treated every non-first option step as unlimited.
 *  - The "Other…" free-text slot is keyed by the option step's id
 *    (`OptionSchema.isOther`), supporting any number of Other slots on any
 *    step — replacing the three fixed slots keyed by position 0/1/2.
 */

import type { Cart, FlowConfig, Option, Selections, Step } from '../types';
import type { SubmissionOrderStep } from '@brandbox/content-schema';
import {
  getMaxSelections as resolveMaxSelections,
  isOtherOption,
  isSingleSelect,
  isUnlimitedSelection,
} from '@brandbox/content-schema';
import { isOptionsStep } from './flowSteps';

// Re-export the canonical "Other…" predicate so public consumers import it
// from the order-semantics hub (one place) rather than re-deriving the rule.
export { isOtherOption } from '@brandbox/content-schema';

/** Every option-type step in the flow, in order. */
export function getOptionSteps(flow: FlowConfig): Step[] {
  return flow.steps.filter(isOptionsStep);
}

/**
 * The ONE selection-cap convention for an option step, the single source of
 * truth every consumer reads (the badge, `isMultiSelectStep`, `toggleOption`,
 * the cap-exceeded modals, and `CheckoutPage`):
 *
 *   `0` OR absent → UNLIMITED (Infinity)
 *   `1`           → single-select (radio)
 *   `N` (N ≥ 2)   → hard cap of N
 *
 * Why absent = unlimited (not single): this restores Spring/Office to their
 * LIVE behaviour. The original `App.tsx` hardcoded merch + final-touch as
 * unlimited; the live `flow-pasen.json`/`flow-office.json` now set those steps
 * to `maxSelections: 0` to encode "unlimited" under this one rule. An
 * admin-authored step always carries a `maxSelections` (the schema makes it
 * required); a bundled/legacy doc that omits it is treated as unlimited, never
 * silently capped to one — the safe direction for a customer-facing order.
 */
export function getMaxSelections(step: Step): number {
  return resolveMaxSelections(step);
}

/** True when the step allows unlimited selections (`maxSelections` 0/absent). */
export function isUnlimitedStep(step: Step): boolean {
  return isUnlimitedSelection(step);
}

/** True when the step allows exactly one selection. */
export function isSingleSelectStep(step: Step): boolean {
  return isSingleSelect(step);
}

/**
 * Generic key for a step's "Other…" free-text value. We key by step id so a
 * box can carry any number of Other slots across any number of steps (the
 * legacy code had three fixed slots: packagingOther / merchOther /
 * finalTouchOther). The configurator stores these in a
 * `Record<stepId, string>`.
 */
export function getOtherKey(step: Step): string {
  return step.id;
}

/** True iff the step has at least one option flagged as a first-class "Other…". */
export function stepHasOtherOption(step: Step): boolean {
  return (step.options ?? []).some((o) => o.isOther);
}

/**
 * The map of per-step "Other…" free-text values, keyed by step id. The
 * configurator threads this through cart → submission → email.
 */
export type OtherValues = Record<string, string>;

/**
 * The legacy `cart.box` shape, enriched with the generic per-step
 * representation. The legacy fields stay populated (positional projection)
 * so the existing submission/email path is byte-identical for Spring/Office;
 * `stepSelections` is the generic source used by the submission builder.
 */
export interface DerivedCartBox {
  themeId: string;
  boxName: string;
  packagingId: string;
  packagingOther: string;
  merchIds: string[];
  merchOther: string;
  finalTouchIds: string[];
  finalTouchOther: string;
}

/**
 * Project the flow's selections into the legacy `cart.box` slots by OPTION
 * STEP POSITION:
 *   slot 0 → packaging{Id,Other}
 *   slot 1 → merch{Ids,Other}
 *   slot 2 → finalTouch{Ids,Other}
 *
 * For Spring/Office (packaging → merch → final-touch) this reproduces the old
 * literal-keyed mapping exactly. For a novel box the same positions are
 * filled, so checkout opens and the submission is non-empty. Steps beyond the
 * third have no legacy slot — they're carried only in the generic
 * `order.steps[]` (see `buildSubmissionSteps`).
 */
export function deriveCartBox(
  flow: FlowConfig,
  selections: Selections,
  otherValues: OtherValues,
  themeId: string,
  boxName: string,
): DerivedCartBox {
  const optionSteps = getOptionSteps(flow);
  const slot = (i: number): { ids: string[]; other: string } => {
    const step = optionSteps[i];
    if (!step) return { ids: [], other: '' };
    return {
      ids: selections[step.id] ?? [],
      other: otherValues[step.id] ?? '',
    };
  };
  const s0 = slot(0);
  const s1 = slot(1);
  const s2 = slot(2);
  return {
    themeId,
    boxName,
    packagingId: s0.ids[0] ?? '',
    packagingOther: s0.other,
    merchIds: s1.ids,
    merchOther: s1.other,
    finalTouchIds: s2.ids,
    finalTouchOther: s2.other,
  };
}

/**
 * Whether the box has enough selected to proceed to checkout. Structure-
 * driven gate: the first option step (slot 0) must have a selection or an
 * Other free-text, AND the second option step (slot 1) must have at least one
 * selection or an Other free-text.
 *
 * For Spring/Office this is exactly the legacy `packagingId && merchIds.length`
 * gate (with the Other free-text fallbacks the CheckoutPage already honoured).
 * For a box with fewer than two option steps, only the present slots are
 * required (a single-option-step box is checkout-ready once that step is
 * filled).
 */
export function isCheckoutReady(flow: FlowConfig, box: Cart['box']): boolean {
  const optionSteps = getOptionSteps(flow);
  const slot0Ok = !optionSteps[0] || !!box.packagingId || !!box.packagingOther;
  const slot1Ok =
    !optionSteps[1] || box.merchIds.length > 0 || !!box.merchOther;
  return slot0Ok && slot1Ok;
}

/**
 * Build the generic, self-describing `order.steps[]` array: every option step
 * that has at least one selection, keyed by its real id + title, with each
 * selected option's id + label, and the step's "Other…" typed value attached
 * to the selected Other option as `customText`.
 */
export function buildSubmissionSteps(
  flow: FlowConfig,
  selections: Selections,
  otherValues: OtherValues,
): SubmissionOrderStep[] {
  const out: SubmissionOrderStep[] = [];
  for (const step of getOptionSteps(flow)) {
    const selectedIds = selections[step.id] ?? [];
    if (selectedIds.length === 0) continue;
    const otherText = otherValues[step.id] ?? '';
    const items = selectedIds
      .map((id) => {
        const option = step.options?.find((o) => o.id === id);
        const label = option?.label ?? id;
        // "Other…" detection (first-class flag + legacy id convention) is owned
        // by `isOtherOption` so every consumer decides identically.
        return isOtherOption(option ?? { id }) && otherText
          ? { id, label, customText: otherText }
          : { id, label };
      });
    out.push({ stepId: step.id, title: step.title, items });
  }
  return out;
}

/**
 * One selected option, resolved against the option step that owns its
 * positional slot. The same `label` / `isOther` / `customText` resolution the
 * checkout list, the cart drawer, and the submission builder each used to
 * re-derive — computed once.
 *
 *  - `label`        the option's authored label, falling back to its id (the
 *                   raw-id fallback every reader already used).
 *  - `isOther`      whether this option is the slot's "Other…" (owned by
 *                   `isOtherOption` — first-class flag + legacy id convention).
 *  - `customText`   the slot's typed "Other…" free-text, only when this is the
 *                   Other option (empty string otherwise).
 *  - `displayLabel` what a reader shows: the customer's free-text wins, else
 *                   the authored label, else the id.
 *  - `option`       the resolved `Option` (or `undefined` when the slot's step
 *                   has no such option), so a reader that spreads the full
 *                   option (image/price/minBoxes) keeps doing so byte-identically.
 */
export interface PositionalSlotItem {
  id: string;
  label: string;
  isOther: boolean;
  customText: string;
  displayLabel: string;
  option: Option | undefined;
}

/**
 * The resolved view of ONE legacy positional slot (0 = packaging, 1 = merch,
 * 2 = final-touch). Owns "which option step is this slot, and what are its
 * resolved selected items + Other free-text" — the duplicated derivation the
 * three public readers each re-walked.
 *
 *  - `slot`     the position (0/1/2).
 *  - `step`     the option step that owns this slot, or `undefined` when the
 *               flow has fewer than `slot + 1` option steps.
 *  - `stepId`   the owning step's id (for the slot→stepId map the Other
 *               free-text accessors key on); `undefined` when no step.
 *  - `title`    the owning step's title; `undefined` when no step. Readers add
 *               their own casing + literal fallback (a view concern that
 *               deliberately differs: the checkout uppercases, the drawer does
 *               not).
 *  - `otherText`the slot's typed "Other…" free-text.
 *  - `items`    the slot's selected options, resolved (see PositionalSlotItem).
 */
export interface PositionalSlot {
  slot: number;
  step: Step | undefined;
  stepId: string | undefined;
  title: string | undefined;
  otherText: string;
  items: PositionalSlotItem[];
}

/** The three legacy positional slots, in order. */
const POSITIONAL_SLOT_COUNT = 3;

/** Resolve one selected option id against the option step that owns its slot. */
function resolveSlotItem(
  step: Step | undefined,
  id: string,
  otherText: string,
): PositionalSlotItem {
  const option = step?.options?.find((o) => o.id === id);
  const label = option?.label ?? id;
  const isOther = isOtherOption(option ?? { id });
  const customText = isOther ? otherText : '';
  return {
    id,
    label,
    isOther,
    customText,
    // The customer's free-text wins, then the authored label, then the id —
    // the resolution the checkout's Other line item used.
    displayLabel: (isOther && otherText) || label,
    option,
  };
}

/**
 * Project a cart box's selections onto the three legacy positional slots,
 * resolving each slot's owning option step + its selected items' labels +
 * "Other…" free-text ONCE. CheckoutPage, ShoppingCart, and useConfigurator
 * consume this instead of each re-walking `getOptionSteps(flow)`, indexing
 * `optionSteps[slot]`, and re-detecting Other.
 *
 * Slot → cart-box field:
 *   0 → packagingId (single)   / packagingOther
 *   1 → merchIds (many)        / merchOther
 *   2 → finalTouchIds (many)   / finalTouchOther
 *
 * READ-SIDE ONLY. This derives nothing new and persists nothing — the emailed/
 * persisted positional contract (ADR 0001) is built by `deriveCartBox` /
 * `buildSubmissionSteps` and is unchanged. Slots with no owning option step
 * (a flow with fewer than three option steps) carry `step: undefined` and an
 * empty `items` array, so readers fall back exactly as before.
 */
export function buildPositionalSlots(
  flow: FlowConfig,
  box: Cart['box'],
): PositionalSlot[] {
  const optionSteps = getOptionSteps(flow);
  const selectedIds: string[][] = [
    box.packagingId ? [box.packagingId] : [],
    box.merchIds,
    box.finalTouchIds,
  ];
  const otherTexts: string[] = [
    box.packagingOther ?? '',
    box.merchOther ?? '',
    box.finalTouchOther ?? '',
  ];
  const slots: PositionalSlot[] = [];
  for (let slot = 0; slot < POSITIONAL_SLOT_COUNT; slot++) {
    const step = optionSteps[slot];
    const otherText = otherTexts[slot];
    slots.push({
      slot,
      step,
      stepId: step?.id,
      title: step?.title,
      otherText,
      items: selectedIds[slot].map((id) => resolveSlotItem(step, id, otherText)),
    });
  }
  return slots;
}
